import React, { useState, useEffect } from 'react';
import { useTeam } from '../contexts/TeamContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Calendar, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';

/**
 * QuickActions Component
 * Provides consistent quick action buttons with functional dialogs
 * HIGH CONTRAST - explicitly styled for accessibility
 */
const QuickActions = ({ position = 'top' }) => {
  const { currentTeam } = useTeam();
  const baseClasses = "flex gap-2";
  const containerClasses = position === 'top' 
    ? `${baseClasses} justify-end`
    : `${baseClasses} justify-center mt-8 mb-4`;

  if (!currentTeam) {
    return null;
  }

  return (
    <div className={containerClasses}>
      <CreateTaskDialog />
      <CreateAppointmentDialog />
      <SendMessageDialog />
    </div>
  );
};

// Create Task Dialog
const CreateTaskDialog = () => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assigned_to: '',
    dashboard_area: 'medical',
    repeat_frequency: 'none',
    repeat_until: ''
  });

  useEffect(() => {
    if (open && currentTeam) {
      fetchTeamMembers();
    }
  }, [open, currentTeam]);

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/members`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const flatMembers = data.map(item => ({
          user_id: item.user.id,
          user_name: item.user.name,
          role: item.membership.role
        }));
        setTeamMembers(flatMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        assigned_to: formData.assigned_to || null,
        shared_with_support: false,
        shared_with_providers: false,
        repeat_frequency: formData.repeat_frequency === 'none' ? null : formData.repeat_frequency,
        repeat_until: formData.repeat_until ? new Date(formData.repeat_until).toISOString() : null
      };

      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Task created successfully');
        setOpen(false);
        setFormData({
          title: '',
          description: '',
          due_date: '',
          priority: 'medium',
          assigned_to: '',
          dashboard_area: 'medical',
          repeat_frequency: 'none',
          repeat_until: ''
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      console.error('Create task error:', error);
      toast.error('Error creating task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          className="bg-blue-600 hover:bg-blue-700 text-white border-2 border-blue-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Pick up prescription"
              required
              className="mt-1 border-gray-300"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add details about this task..."
              className="mt-1 border-gray-300"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="priority">Priority *</Label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="mt-1 border-gray-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned_to">Assign To</Label>
              <select
                id="assigned_to"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
              >
                <option value="">Unassigned</option>
                {teamMembers.map(member => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.user_name} ({member.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="dashboard_area">Category *</Label>
              <select
                id="dashboard_area"
                value={formData.dashboard_area}
                onChange={(e) => setFormData({ ...formData, dashboard_area: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
                required
              >
                <option value="medical">Medical</option>
                <option value="financial">Financial</option>
                <option value="legal">Legal</option>
                <option value="household">Household</option>
                <option value="caregiving">Caregiving</option>
                <option value="end_of_life">End of Life</option>
              </select>
            </div>
          </div>

          {/* Recurring Task Options */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold mb-3 text-gray-900">Recurring Task (Optional)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="repeat_frequency">Repeat Frequency</Label>
                <select
                  id="repeat_frequency"
                  value={formData.repeat_frequency}
                  onChange={(e) => setFormData({ ...formData, repeat_frequency: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
                >
                  <option value="none">Does Not Repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 Weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {formData.repeat_frequency !== 'none' && (
                <div>
                  <Label htmlFor="repeat_until">Repeat Until (Optional)</Label>
                  <Input
                    id="repeat_until"
                    type="date"
                    value={formData.repeat_until}
                    onChange={(e) => setFormData({ ...formData, repeat_until: e.target.value })}
                    className="mt-1 border-gray-300"
                    min={formData.due_date ? formData.due_date.split('T')[0] : ''}
                  />
                  <p className="text-xs text-gray-600 mt-1">Leave blank for indefinite</p>
                </div>
              )}
            </div>
            {formData.repeat_frequency !== 'none' && formData.due_date && (
              <p className="text-sm text-blue-700 mt-2">
                This will create recurring tasks starting from the due date
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-700 hover:bg-blue-800">
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Appointment Dialog
const CreateAppointmentDialog = () => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    datetime: '',
    location: '',
    notes: '',
    dashboard_area: 'medical'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        datetime: new Date(formData.datetime).toISOString()
      };

      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Appointment created successfully');
        setOpen(false);
        setFormData({
          title: '',
          datetime: '',
          location: '',
          notes: '',
          dashboard_area: 'medical'
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create appointment');
      }
    } catch (error) {
      console.error('Create appointment error:', error);
      toast.error('Error creating appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          className="bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Add Appointment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Appointment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="appt-title">Appointment Title *</Label>
            <Input
              id="appt-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Doctor Visit"
              required
              className="mt-1 border-gray-300"
            />
          </div>

          <div>
            <Label htmlFor="datetime">Date & Time *</Label>
            <Input
              id="datetime"
              type="datetime-local"
              value={formData.datetime}
              onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
              required
              className="mt-1 border-gray-300"
            />
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Main Street Clinic"
              className="mt-1 border-gray-300"
            />
          </div>

          <div>
            <Label htmlFor="appt-category">Category *</Label>
            <select
              id="appt-category"
              value={formData.dashboard_area}
              onChange={(e) => setFormData({ ...formData, dashboard_area: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
              required
            >
              <option value="medical">Medical</option>
              <option value="financial">Financial</option>
              <option value="legal">Legal</option>
              <option value="household">Household</option>
              <option value="caregiving">Caregiving</option>
            </select>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any additional details..."
              className="mt-1 border-gray-300"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-700 hover:bg-blue-800">
              {loading ? 'Creating...' : 'Create Appointment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Send Message Dialog
const SendMessageDialog = () => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    audience: 'all'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/broadcasts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Message sent successfully');
        setOpen(false);
        setFormData({
          title: '',
          content: '',
          audience: 'all'
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Error sending message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          className="bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50"
        >
          <Mail className="w-4 h-4 mr-2" />
          Send Message
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Team Message</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="msg-title">Subject *</Label>
            <Input
              id="msg-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Update on Dad's condition"
              required
              className="mt-1 border-gray-300"
            />
          </div>

          <div>
            <Label htmlFor="content">Message *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Type your message to the team..."
              required
              className="mt-1 border-gray-300"
              rows={6}
            />
          </div>

          <div>
            <Label htmlFor="audience">Send To *</Label>
            <select
              id="audience"
              value={formData.audience}
              onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
              required
            >
              <option value="all">Everyone (All Team Members)</option>
              <option value="inner_circle">Inner Circle Only</option>
              <option value="support_team">Support Team</option>
              <option value="providers">Providers Only</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-700 hover:bg-blue-800">
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickActions;
